import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; // Added import

const CBDropdown = ({
  overlay, // The menu component (ReactNode)
  children, // The trigger element
  trigger = ['hover'], // 'hover', 'click'
  placement = 'bottomLeft', // Basic placement, more complex needed for full support
  className = '',
}) => {
  const [visible, setVisible] = useState(false);
  const wrapperRef = useRef(null);
  const overlayRef = useRef(null);
  const timeoutRef = useRef(null);

  const isClick = trigger.includes('click');
  const isHover = trigger.includes('hover');

  const showOverlay = () => {
    clearTimeout(timeoutRef.current);
    setVisible(true);
  };

  const hideOverlay = (delay = 150) => { // Add delay for hover
    if (isHover) {
        timeoutRef.current = setTimeout(() => {
             // Check if mouse is still over wrapper or overlay before hiding
             // A more robust check might involve relatedTarget in blur/focusout events
             if (wrapperRef.current && overlayRef.current &&
                 !wrapperRef.current.contains(document.activeElement) &&
                 !overlayRef.current.contains(document.activeElement) && // Check activeElement for focus
                 !wrapperRef.current.matches(':hover') &&
                 !overlayRef.current.matches(':hover') // Check hover state
                ) {
                 setVisible(false);
             }
        }, delay);
    } else {
        setVisible(false);
    }
  };

  const toggleOverlay = (e) => {
     e.stopPropagation(); // Prevent body click when clicking trigger
     setVisible(!visible);
  };

   // Close on outside click
   useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        // Only hide on outside click if it's a click trigger or if not hovering
         if (isClick || !isHover || (isHover && !wrapperRef.current.matches(':hover') && (!overlayRef.current || !overlayRef.current.matches(':hover')))) {
             setVisible(false);
         }
      }
    }
    if (visible) { // Only add listener when visible
        document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      clearTimeout(timeoutRef.current); // Clear timeout on unmount or visibility change
    };
  }, [wrapperRef, overlayRef, visible, isClick, isHover]); // Add overlayRef and triggers to dependencies


  const triggerProps = {};
  if (isClick) {
    triggerProps.onClick = toggleOverlay;
  }
  if (isHover) {
    triggerProps.onMouseEnter = showOverlay;
    triggerProps.onMouseLeave = () => hideOverlay();
     // Keep open if focusing within
    triggerProps.onFocus = showOverlay;
    triggerProps.onBlur = () => hideOverlay(0); // Hide immediately on blur if not hovering
  }

   const overlayProps = {};
   if (isHover) {
       overlayProps.onMouseEnter = showOverlay; // Keep open when hovering onto overlay
       overlayProps.onMouseLeave = () => hideOverlay();
       // Keep open if focusing within overlay
       overlayProps.onFocus = showOverlay;
       overlayProps.onBlur = () => hideOverlay(0);
   }


  // Basic placement logic (adjust as needed)
  let placementClasses = 'origin-top-left left-0'; // Default: bottomLeft
  if (placement === 'bottomRight') placementClasses = 'origin-top-right right-0';
  if (placement === 'topLeft') placementClasses = 'origin-bottom-left left-0 bottom-full mb-1';
  if (placement === 'topRight') placementClasses = 'origin-bottom-right right-0 bottom-full mb-1';
  // Add more placements (top, bottom, left, right centers)

  return (
    <div ref={wrapperRef} className={`relative inline-block ${className}`}>
      {/* Wrap children in a span and apply triggerProps to the wrapper */}
      <span {...triggerProps} style={{ display: 'inline-block' }} className="cbd-dropdown-trigger-wrapper"> {/* Added wrapper */}
        {children}
      </span>
      {visible && (
        <div
          ref={overlayRef}
          className={`absolute z-50 mt-1 w-auto bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${placementClasses}`}
          {...overlayProps} // Add hover/focus handlers to overlay itself
          tabIndex="-1" // Make it focusable for blur handling
        >
          {/* Inject hideOverlay into menu item clicks if possible, or rely on outside click */}
          {/* A better approach might be context or render props for menu items */}
          {React.cloneElement(overlay, { onItemClick: () => hideOverlay(0) })}
        </div>
      )}
    </div>
  );
};


// Basic Menu/MenuItem structure needed for the overlay
const CBMenu = ({ children, className = '', onClick, onItemClick }) => { // Accept onItemClick from Dropdown
    // Inject onClick into children
    const childrenWithOnClick = React.Children.map(children, child => {
        if (React.isValidElement(child)) {
            return React.cloneElement(child, {
                // Pass a function that calls the original onClick AND the menu's onClick AND the dropdown's onItemClick
                onClick: (e) => {
                    if (child.props.onClick) {
                        child.props.onClick(e); // Call original item onClick if exists
                    }
                    if (onClick) {
                        // Pass item key or necessary info to menu's onClick
                        onClick({ key: child.key, domEvent: e });
                    }
                    if (onItemClick) { // Call dropdown's hide function
                        onItemClick();
                    }
                }
            });
        }
        return child;
    });

    return <div className={`py-1 ${className}`} role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
        {childrenWithOnClick}
    </div>;
};

const CBMenuItem = ({ children, className = '', iconDef, disabled, onClick, ...props }) => ( // Changed icon to iconDef
  <button
    {...props} // Pass other props like key
    onClick={onClick} // Use the potentially injected onClick
    disabled={disabled}
    className={`flex items-center w-32 text-left px-4 py-2 text-sm ${ // Added flex items-center
      disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
    } ${className}`}
    role="menuitem"
  >
    {/* Render FontAwesomeIcon internally if iconDef is provided */}
    {iconDef && <FontAwesomeIcon icon={iconDef} className="mr-2 w-4 h-4" />} {/* Added size constraint */}
    {children}
  </button>
);


export { CBDropdown, CBMenu, CBMenuItem }; // Export Menu/MenuItem too